import PMBInputNumber from 'components/PMBInputNumber'
import { capitalizeEveryWord, parseNumber } from 'helpers'
import { useEffect, useMemo, useState } from 'react'
import { Minus, Plus } from 'react-feather'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
} from 'reactstrap'
import { parseFormattedNumber } from 'utility/Utils'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { useGetSchemaDetailQuery } from '../map-schema/mapSchemaApiSlice'

const numberOrZero = (val) => {
  const parsed = parseFloat(val)
  if (Number.isNaN(parsed)) return 0
  return parsed < 0 ? 0 : parsed
}

const ModalCalculate = ({
  data: dataSchema,
  dataProject,
  isOpen,
  toggle,
  inventoryMap = {},
}) => {
  const { data, isLoading } = useGetSchemaDetailQuery(dataSchema?.id, {
    skip: !dataSchema?.id,
  })

  const baseOutputs = useMemo(() => data?.output || [], [data])
  const baseInputs = useMemo(() => data?.input || [], [data])

  const [outputs, setOutputs] = useState([])
  const [inputs, setInputs] = useState([])

  // Get initial target for an output item: prefer goals amount, fallback to base
  const getInitialTarget = (outputItem) => {
    const goal = dataProject?.goals?.find((g) => g.itemId === outputItem.itemId)
    return goal ? numberOrZero(goal.amount) : numberOrZero(outputItem.amount)
  }

  const getCurrentInventory = (outputItem) => {
    if (!outputItem?.itemId) return 0
    const value = inventoryMap[outputItem.itemId]
    if (typeof value === 'number') return numberOrZero(value)
    if (value && typeof value === 'object') {
      return numberOrZero(value.totalInventory ?? value.currentAmount ?? 0)
    }
    return 0
  }

  const getRemainingTarget = (outputItem) => {
    const target = getInitialTarget(outputItem)
    const current = getCurrentInventory(outputItem)
    return Math.max(0, target - current)
  }

  const initializeFromSchema = () => {
    if (!baseOutputs.length) return

    const primaryBase = numberOrZero(baseOutputs[0]?.amount)
    const primaryRemaining = getRemainingTarget(baseOutputs[0])
    const primaryTarget = getInitialTarget(baseOutputs[0])
    const desiredOutput =
      primaryRemaining > 0 ? primaryRemaining : primaryTarget
    const initialRatio = primaryBase > 0 ? desiredOutput / primaryBase : 1

    setOutputs(
      baseOutputs.map((o) => {
        const goal = dataProject?.goals?.find((g) => g.itemId === o.itemId)
        const targetAmount = goal ? numberOrZero(goal.amount) : 0
        const currentInventory = getCurrentInventory(o)
        return {
          ...o,
          baseAmount: numberOrZero(o.amount),
          amount: numberOrZero(o.amount) * initialRatio,
          targetAmount,
          currentInventory,
        }
      }),
    )
    setInputs(
      baseInputs.map((i) => ({
        ...i,
        baseReqAmount: numberOrZero(i.reqAmount),
        amount: numberOrZero(i.reqAmount) * initialRatio,
      })),
    )
  }

  useEffect(() => {
    initializeFromSchema()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataSchema?.id, dataProject, inventoryMap])

  const handleOutputChange = (index, val) => {
    const nextVal = numberOrZero(val)
    const baseAmt = numberOrZero(outputs[index]?.baseAmount)
    const newRatio = baseAmt > 0 ? nextVal / baseAmt : 0

    setOutputs((prev) =>
      prev.map((o, i) =>
        i === index
          ? { ...o, amount: nextVal }
          : { ...o, amount: numberOrZero(o.baseAmount) * newRatio },
      ),
    )
    setInputs((prev) =>
      prev.map((inp) => ({
        ...inp,
        amount: numberOrZero(inp.baseReqAmount) * newRatio,
      })),
    )
  }

  const handleOutputIncrement = (index, delta) => {
    const current = parseNumber(outputs[index]?.amount)
    const newVal = Math.max(0, current + delta)
    handleOutputChange(index, newVal)
  }

  const handleInputChange = (index, val) => {
    const nextVal = numberOrZero(val)
    const baseReq = numberOrZero(inputs[index]?.baseReqAmount)
    const newRatio = baseReq > 0 ? nextVal / baseReq : 0

    setInputs((prev) =>
      prev.map((inp, i) =>
        i === index
          ? { ...inp, amount: nextVal }
          : { ...inp, amount: numberOrZero(inp.baseReqAmount) * newRatio },
      ),
    )
    setOutputs((prev) =>
      prev.map((o) => ({
        ...o,
        amount: numberOrZero(o.baseAmount) * newRatio,
      })),
    )
  }

  const handleInputIncrement = (index, delta) => {
    const current = parseNumber(inputs[index]?.amount)
    const newVal = Math.max(0, current + delta)
    handleInputChange(index, newVal)
  }

  const sectionScrollStyle = {
    maxHeight: '220px',
    overflowY: 'scroll',
    // give a consistent visual hint for scrollbars across browsers
    scrollbarWidth: 'thin',
    msOverflowStyle: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingRight: '6px',
  }

  return (
    <Modal
      centered
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      data-testid="project-modal-calculate"
    >
      <ModalHeader toggle={toggle}>
        <div className="w-100">
          <div className="d-flex align-items-center gap-2">
            <span>
              Skema {data?.createMethodLabel || dataSchema?.createMethodLabel}
            </span>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {isLoading ? (
          <div className="py-4 text-center">Memuat skema...</div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {/* === OUTPUT SECTION === */}
            <h6 className="fw-semibold mb-0">Output</h6>
            <Card className="shadow-sm border-0">
              <div style={sectionScrollStyle}>
                {outputs.map((o, i) => (
                  <Card
                    key={o.itemId}
                    className="shadow-sm border-0 mb-0"
                    data-testid={`project-calc-output-row-${i}`}
                  >
                    <CardBody>
                      <div className="d-flex gap-2 flex-column">
                        <h6 className="fw-bold mb-0">{o?.nameItem}</h6>
                        <div className="d-flex gap-2 flex-wrap">
                          <Badge
                            size="sm"
                            disabled
                            className="badge-blues mt-1"
                          >
                            Default:{' '}
                            {newFormatThousandSeparator(o?.baseAmount || 0)}{' '}
                            {capitalizeEveryWord(o?.expectedUnit || o?.unit)}
                          </Badge>
                          {o?.targetAmount > 0 && (
                            <Badge
                              size="sm"
                              disabled
                              className="badge-blues mt-1"
                            >
                              Target:{' '}
                              {newFormatThousandSeparator(o?.targetAmount || 0)}{' '}
                              {capitalizeEveryWord(o?.expectedUnit || o?.unit)}
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            color="light"
                            onClick={() => handleOutputIncrement(i, -1)}
                          >
                            <Minus size={16} />
                          </Button>

                          <PMBInputNumber
                            onFocus={(e) => e.target.select()}
                            name={`calc-output-${i}`}
                            placeholder="Jumlah"
                            prefix={capitalizeEveryWord(
                              o?.expectedUnit || o?.unit,
                            )}
                            isFirstPrefix={false}
                            value={newFormatThousandSeparator(o?.amount || 0)}
                            onChange={(e) => {
                              const numericValue = parseFormattedNumber(
                                e.target.value,
                              )
                              handleOutputChange(i, numericValue)
                            }}
                          />

                          <Button
                            color="light"
                            onClick={() => handleOutputIncrement(i, 1)}
                          >
                            <Plus size={16} />
                          </Button>
                        </div>
                        {(() => {
                          const target = numberOrZero(o?.targetAmount)
                          if (target <= 0) return null
                          const produced =
                            numberOrZero(o?.currentInventory) +
                            numberOrZero(o?.amount)
                          const remaining = Math.max(0, target - produced)
                          const pct =
                            target > 0
                              ? Math.min(100, (produced / target) * 100)
                              : 0
                          return (
                            <span className="text-muted small">
                              Sisa target:{' '}
                              {newFormatThousandSeparator(remaining)} /{' '}
                              {newFormatThousandSeparator(target)} (
                              {Number.isFinite(pct) ? pct.toFixed(0) : '0'}%
                              tercapai)
                            </span>
                          )
                        })()}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </Card>
            {/* === INPUT SECTION === */}
            <h6 className="fw-semibold mb-0 mt-2">Input</h6>
            <Card className="shadow-sm border-0">
              <div style={sectionScrollStyle}>
                {inputs.map((inp, i) => (
                  <Card
                    key={inp.itemId}
                    className="shadow-sm border-0 mb-0"
                    data-testid={`project-calc-input-row-${i}`}
                  >
                    <CardBody>
                      <div className="d-flex gap-2 flex-column">
                        <div>
                          <h6 className="fw-bold mb-0">{inp?.nameItem}</h6>
                          <Badge
                            size="sm"
                            disabled
                            className="badge-blues mt-1"
                          >
                            Default:{' '}
                            {newFormatThousandSeparator(
                              inp?.baseReqAmount || 0,
                            )}{' '}
                            {capitalizeEveryWord(
                              inp?.expectedUnit || inp?.unit,
                            )}
                          </Badge>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Button
                            color="light"
                            onClick={() => handleInputIncrement(i, -1)}
                          >
                            <Minus size={16} />
                          </Button>

                          <PMBInputNumber
                            onFocus={(e) => e.target.select()}
                            name={`calc-input-${i}`}
                            placeholder="Jumlah"
                            prefix={capitalizeEveryWord(
                              inp?.expectedUnit || inp?.unit,
                            )}
                            isFirstPrefix={false}
                            value={newFormatThousandSeparator(inp?.amount || 0)}
                            onChange={(e) => {
                              const numericValue = parseFormattedNumber(
                                e.target.value,
                              )
                              handleInputChange(i, numericValue)
                            }}
                          />

                          <Button
                            color="light"
                            onClick={() => handleInputIncrement(i, 1)}
                          >
                            <Plus size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </Card>
            {/* === RINGKASAN INPUT === */}
            <Card className="shadow-sm border-0 mb-0 mt-2">
              <CardBody>
                <h6 className="fw-semibold mb-3">Ringkasan Kebutuhan Input</h6>
                {inputs.map((inp) => (
                  <div
                    key={inp.itemId}
                    className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                  >
                    <span className="fw-semibold">{inp?.nameItem}</span>
                    <span className="fw-bold text-primary">
                      {newFormatThousandSeparator(inp?.amount || 0)}{' '}
                      {capitalizeEveryWord(inp?.expectedUnit || inp?.unit)}
                    </span>
                  </div>
                ))}
              </CardBody>
            </Card>
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default ModalCalculate
